import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import toast from "react-hot-toast";
import {
  MdLocationOn, MdOutlinePeople, MdWorkOutline, MdCalendarToday,
  MdOutlineKeyboardArrowDown,
} from "react-icons/md";
import { RiPencilLine, RiEyeLine } from "react-icons/ri";
import { BsFillTrashFill, BsDownload, BsExclamationCircleFill } from "react-icons/bs";
import { ArrowLeft, Share2, UserPlus } from "lucide-react";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import PopupModal from "../../PopupModal";
import SlidingModal from "../../SlidingModal/slidingModal";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import {
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../../StyledCss/styledInputs";
import AddCandidateModal from "../AddCandidateModal";
import UploadResumeModal from "../UploadResumeModal";
import ResumeReviewModal from "../ResumeReviewModal";
import BulkUploadModal from "../BulkUploadModal";
import ImportResumesModal from "../ImportResumesModal";
import ResumeImportPanel from "../ResumeImportPanel";
import ReferCandidateModal from "../ReferCandidateModal";
import ShareJobModal from "../ShareJobModal";
import {
  getHiringJobById,
  getHiringApplications,
  getHiringCandidateRanking,
  updateHiringApplicationStatus,
  deleteHiringApplication,
} from "../../../api/services/hroneApi";
import {
  normalizeJob,
  normalizeApplication,
  sortApplications,
  SORT_OPTIONS,
  getAtsMatchBadgeClass,
  IMPORT_STATUS,
} from "../../../util/hiringUtils";
import { fetchImportSummary } from "../../../api/services/resumeImportService";
import { loadImportState, saveImportState } from "../../../util/resumeImportStorage";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
import { getHiringListPath, getHiringListLabel, resolveFromHiringTab } from "../../../util/hiringNav";
import "./index.css";
import "../AtsBreakdownDrawer/index.css";

/* ─── Page-level tab config ─────────────────────────── */

const DETAIL_TABS = [
  { id: "OVERVIEW",      label: "Overview",         path: "" },
  { id: "APPLICATIONS",  label: "Ready Candidates", path: "/applications" },
  { id: "NEEDS_REVIEW",  label: "Needs Review",     path: "/needs-review" },
  { id: "DUPLICATE",     label: "Duplicates",       path: "/duplicate" },
  { id: "FAILED",        label: "Failed",           path: "/failed" },
];

const IMPORT_TAB_IDS = new Set(["NEEDS_REVIEW", "DUPLICATE", "FAILED"]);

const resolveActiveTab = (pathname) => {
  if (pathname.endsWith("/applications"))  return "APPLICATIONS";
  if (pathname.endsWith("/needs-review"))  return "NEEDS_REVIEW";
  if (pathname.endsWith("/duplicate"))     return "DUPLICATE";
  if (pathname.endsWith("/failed"))        return "FAILED";
  return "OVERVIEW";
};

const IMPORT_TAB_STATUS = {
  NEEDS_REVIEW: IMPORT_STATUS.NEEDS_REVIEW,
  DUPLICATE:    IMPORT_STATUS.DUPLICATE,
  FAILED:       IMPORT_STATUS.FAILED,
};

/* ─── Constants ─────────────────────────────────────── */

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const STATUS_SELECT_BG = "#e3f2fd";

const SORT_API_MAP = {
  HIGHEST_SCORE: "highest_score",
  LOWEST_SCORE:  "lowest_score",
  EXPERIENCE:    "experience",
};

/* ─── Helpers ───────────────────────────────────────── */

const formatPostedDate = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

// job.publishedDate is stored as "DD-MM-YYYY"; compares calendar dates only (ignores time of day).
const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  const [day, month, year] = dateStr.split("-").map((n) => parseInt(n, 10));
  const postDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return postDate > today;
};

// Returns why candidates can't be added/referred right now, or null if allowed.
// Used with hiring.candidate.create — not role-name specific.
const getAddCandidateBlockReason = (job) => {
  if (!job) return "Job not found";
  if (job.status === "CLOSED") return "Cannot add or refer candidates to a closed job";
  if (job.status === "DRAFT") return "Cannot add or refer candidates to a draft job";
  if (isFutureDate(job.publishedDate)) return "Cannot add or refer candidates before the job post date";
  return null;
};

// A job with a future post date is still "OPEN" in the backend (that's what
// lets it flip live with no extra action once the date arrives), but showing
// "OPEN" on screen before it's actually accepting candidates is misleading —
// display it as "SCHEDULED" instead, purely a presentation label.
const getDisplayStatus = (job) =>
  job.status === "OPEN" && isFutureDate(job.publishedDate) ? "SCHEDULED" : job.status;

/* ─────────────────────────────────────────────────────── */

const JobDetailsPage = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermission } = usePermissions();
  const canEditCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_EDIT);
  const canDeleteCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_DELETE);
  const canChangeCandidateStatus = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_STATUS_CHANGE);
  const canDownloadResume = hasPermission(FEATURE_CODES.HIRING_RESUME_DOWNLOAD);
  const canCreateCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_CREATE);
  const canImportResume = hasPermission(FEATURE_CODES.HIRING_RESUME_IMPORT);
  const canViewCandidates = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_VIEW);

  const [job, setJob]           = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobNotFound, setJobNotFound] = useState(false);

  const [applications, setApplications]       = useState([]);
  const [appsLoading, setAppsLoading]         = useState(false);
  const [sortOption, setSortOption]           = useState("HIGHEST_SCORE");
  const [refreshAppsTick, setRefreshAppsTick] = useState(0);
  const [deleteModal, setDeleteModal]         = useState({ open: false, appId: null, name: "" });
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);
  const [isUploadResumeOpen, setIsUploadResumeOpen] = useState(false);
  const [parsedResumeData, setParsedResumeData]     = useState(null);
  const [isResumeReviewOpen, setIsResumeReviewOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen]     = useState(false);
  const [isImportOpen, setIsImportOpen]             = useState(false);
  const [addCandidateMenuOpen, setAddCandidateMenuOpen] = useState(false);
  const [isReferOpen, setIsReferOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const addCandidateDropdownRef = useRef(null);
  const [importSessionId, setImportSessionId] = useState(null);
  const [importCounts, setImportCounts]       = useState({
    NEEDS_REVIEW: 0,
    DUPLICATE:    0,
    FAILED:       0,
  });

  const triggerAppsRefresh = () => setRefreshAppsTick((t) => t + 1);

  /* ── Restore import session + fetch counts from API ──
     Skipped without the resume-import permission — the import review tabs
     (Needs Review / Duplicates / Failed) are hidden for that role and the
     summary endpoint would 403 anyway. ── */
  useEffect(() => {
    if (!canImportResume) return undefined;

    const stored = loadImportState(id);
    const initialSessionId = stored?.sessionId || null;

    if (stored?.counts) setImportCounts(stored.counts);
    if (initialSessionId) setImportSessionId(initialSessionId);

    let cancelled = false;
    fetchImportSummary(id, initialSessionId).then((summary) => {
      if (cancelled || !summary) return;
      if (summary.sessionId) setImportSessionId(summary.sessionId);
      if (summary.counts) setImportCounts(summary.counts);
    });

    return () => { cancelled = true; };
  }, [id, refreshAppsTick, canImportResume]);

  const activeTab    = resolveActiveTab(location.pathname);

  /* Tab visibility by permission:
     - Overview: always
     - Ready Candidates: hiring.candidate.view
     - Needs Review / Duplicates / Failed: hiring.resume.import  */
  const visibleTabs = DETAIL_TABS.filter((tab) => {
    if (tab.id === "APPLICATIONS") return canViewCandidates;
    if (IMPORT_TAB_IDS.has(tab.id)) return canImportResume;
    return true;
  });

  /* Landed on a now-hidden tab (stale link / permission removed) →
     fall back to Overview instead of a blank tab. */
  useEffect(() => {
    if (activeTab !== "OVERVIEW" && !visibleTabs.some((tab) => tab.id === activeTab)) {
      navigate(`/hiring/job/${id}`, { replace: true });
    }
  }, [activeTab, visibleTabs, id, navigate]);
  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  /* ── Fetch job from API ── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setJobLoading(true);
      try {
        const res  = await getHiringJobById(id);
        const data = await res.json();
        if (!cancelled) {
          if (data.status) {
            setJob(normalizeJob(data.data));
          } else {
            setJobNotFound(true);
          }
        }
      } catch {
        if (!cancelled) setJobNotFound(true);
      } finally {
        if (!cancelled) setJobLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const loadApplications = useCallback(async () => {
    setAppsLoading(true);
    try {
      const [appsRes, rankRes] = await Promise.all([
        getHiringApplications(id, { limit: 100 }),
        getHiringCandidateRanking(id, { sort: SORT_API_MAP[sortOption] }).catch(() => null),
      ]);
      const appsData  = await appsRes.json();
      let apps = appsData.status
        ? (appsData.data?.applications || []).map(normalizeApplication)
        : [];

      if (rankRes?.ok) {
        const rankData = await rankRes.json();
        const ranked = rankData.data?.applications || rankData.data?.candidates || rankData.data;
        if (rankData.status && Array.isArray(ranked) && ranked.length) {
          const rankedMap = new Map(
            ranked.map((item) => {
              const normalized = normalizeApplication(item);
              return [normalized.id, normalized];
            })
          );
          apps = apps.map((app) => ({ ...app, ...rankedMap.get(app.id) }));
          const rankedIds = ranked.map((item) => item.application_id ?? item.applicationId ?? item.id);
          apps.sort((a, b) => rankedIds.indexOf(a.id) - rankedIds.indexOf(b.id));
        }
      }

      setApplications(sortApplications(apps, sortOption));
    } catch {
      // silently — job still shows even if applications fail to load
    } finally {
      setAppsLoading(false);
    }
  }, [id, sortOption]);

  /* ── Fetch applications from API ──
     Skipped without hiring.candidate.view — the Ready Candidates tab and the
     candidate stat strip are hidden for that role and the endpoint 403s. ── */
  useEffect(() => {
    if (!canViewCandidates) return;
    loadApplications();
  }, [loadApplications, refreshAppsTick, canViewCandidates]);

  useEffect(() => {
    if (!addCandidateMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        addCandidateDropdownRef.current &&
        !addCandidateDropdownRef.current.contains(event.target)
      ) {
        setAddCandidateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [addCandidateMenuOpen]);

  const fromHiringTab  = resolveFromHiringTab(location.state, job?.status);
  const hiringListPath = getHiringListPath(fromHiringTab);
  const navState       = { fromHiringTab };

  /* ── Navigation helpers ──────────────────────────── */

  const handleBack = () => navigate(hiringListPath);

  const goToCandidate = (app, startEdit = false) => {
    navigate(`/hiring/candidate/${app.id}`, {
      state: {
        app,
        jobId: id,
        returnPath: `/hiring/job/${id}/applications`,
        fromHiringTab,
        jobTitle: job?.title,
        jobStatus: job?.status,
        edit: startEdit,
      },
    });
  };

  /* ── Grid handlers ───────────────────────────────── */

  const updateApplicationStatus = async (appId, newStatus) => {
    const prev = applications;
    // Optimistic update
    setApplications((list) =>
      list.map((a) => (a.id === appId ? { ...a, status: newStatus } : a))
    );
    try {
      const res  = await updateHiringApplicationStatus(id, appId, newStatus);
      const data = await res.json();
      if (!data.status) throw new Error(data.message || "Update failed");
      const labels = { SHORTLISTED: "Shortlisted", REJECTED: "Rejected", HIRED: "Hired", APPLIED: "Applied" };
      toast.success(`Status updated to ${labels[newStatus] || newStatus}`);
    } catch {
      setApplications(prev); // revert on failure
      toast.error("Failed to update status. Please try again.");
    }
  };

  const handleRowClicked = (params) => {
    const t = params.event?.target;
    if (!t) return;
    if (t.closest("select") || t.closest("[data-no-row-click]")) return;
    goToCandidate(params.data);
  };

  const handleTabChange = (tab) =>
    navigate(`/hiring/job/${id}${tab.path}`, { state: navState });

  const handleApplicationCreated = (application) => {
    const normalized = normalizeApplication(application);
    setApplications((prev) => sortApplications([normalized, ...prev], sortOption));
    setIsAddCandidateOpen(false);
    toast.success(`${normalized.name || application.name} added successfully`);
  };

  const handleParsedResume = (parsed) => {
    setParsedResumeData(parsed);
    setIsResumeReviewOpen(true);
  };

  const handleReviewSaved = (application) => {
    const normalized = normalizeApplication(application);
    setApplications((prev) => sortApplications([normalized, ...prev.filter((a) => a.id !== normalized.id)], sortOption));
    setIsResumeReviewOpen(false);
    setParsedResumeData(null);
  };

  const handleBulkSaved = (savedApps) => {
    const normalized = savedApps.map(normalizeApplication);
    setApplications((prev) => {
      const merged = [...normalized];
      prev.forEach((app) => {
        if (!merged.some((m) => m.id === app.id)) merged.push(app);
      });
      return sortApplications(merged, sortOption);
    });
    triggerAppsRefresh();
  };

  const handleImportCountsChange = useCallback((status, count) => {
    const key = Object.entries(IMPORT_TAB_STATUS).find(([, v]) => v === status)?.[0];
    if (!key) return;

    setImportCounts((prev) => {
      if (prev[key] === count) return prev;
      const next = { ...prev, [key]: count };
      saveImportState(id, { ...(loadImportState(id) || {}), counts: next });
      return next;
    });
  }, [id]);

  const handleImportComplete = ({
    sessionId,
    savedApplications,
    pendingCandidates,
    failedFiles,
    counts,
    savedTempIds,
  }) => {
    setImportSessionId(sessionId);
    setImportCounts({
      NEEDS_REVIEW: counts?.NEEDS_REVIEW ?? 0,
      DUPLICATE:    counts?.DUPLICATE    ?? 0,
      FAILED:       counts?.FAILED       ?? 0,
    });

    saveImportState(id, {
      sessionId,
      pendingCandidates: pendingCandidates || [],
      failedFiles: failedFiles || [],
      savedTempIds: savedTempIds || [],
      counts: {
        NEEDS_REVIEW: counts?.NEEDS_REVIEW ?? 0,
        DUPLICATE:    counts?.DUPLICATE    ?? 0,
        FAILED:       counts?.FAILED       ?? 0,
      },
    });

    if (savedApplications?.length) {
      handleBulkSaved(savedApplications);
    }

    const targetTab = counts?.NEEDS_REVIEW > 0
      ? "/needs-review"
      : counts?.DUPLICATE > 0
        ? "/duplicate"
        : counts?.FAILED > 0
          ? "/failed"
          : "/applications";

    navigate(`/hiring/job/${id}${targetTab}`, { state: navState });

    // A plain in-memory refetch right after upload can race the backend's
    // duplicate-detection write, same as it did before. A real browser
    // refresh always shows the correct counts, so just do that here too.
    window.location.reload();
  };

  const openManualAddCandidate = () => {
    setAddCandidateMenuOpen(false);
    const blockReason = getAddCandidateBlockReason(job);
    if (blockReason) {
      toast.error(blockReason);
      return;
    }
    setIsAddCandidateOpen(true);
  };

  const openImportResumes = () => {
    setAddCandidateMenuOpen(false);
    const blockReason = getAddCandidateBlockReason(job);
    if (blockReason) {
      toast.error(blockReason);
      return;
    }
    if (activeTab !== "APPLICATIONS") {
      navigate(`/hiring/job/${id}/applications`, { state: navState });
    }
    setIsImportOpen(true);
  };

  const handleDownloadResume = useCallback(async (app) => {
    if (!app.resumeUrl || app.resumeUrl === "#") return;
    try {
      const response = await fetch(app.resumeUrl);
      if (!response.ok) return;
      const blob    = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link    = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `${(app.name || "Resume").replace(/\s+/g, "_")}_Resume.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // silent — no toast
    }
  }, []);

  /* ── Column definitions ──────────────────────────── */

  const columnDefs = useMemo(
    () => [
      { headerName: "CANDIDATE NAME", field: "name",        pinned: "left", width: 180, filter: true, floatingFilter: true, suppressMenu: true },
      { headerName: "EMAIL",          field: "email",        width: 230,     filter: true, floatingFilter: true, suppressMenu: true, tooltipField: "email" },
      { headerName: "MOBILE NUMBER",  field: "mobile",       width: 180,     filter: true, floatingFilter: true, suppressMenu: true },
      {
        headerName: "ATS SCORE", field: "atsScore", width: 170,
        sortable: true, filter: true, floatingFilter: true, suppressMenu: true,
        cellRenderer: (p) => {
          const app = p.data;
          const badgeClass = getAtsMatchBadgeClass(app.atsMatchLabel);
          return (
            <div className="hiring-ats-score-btn">
              <span className="hiring-ats-score-value">{app.atsScore}%</span>
              <span className={badgeClass}>{app.atsMatchLabel}</span>
            </div>
          );
        },
      },
      { headerName: "APPLIED DATE", field: "appliedDate", width: 150, filter: true, floatingFilter: true, suppressMenu: true },
      {
        headerName: "STATUS", field: "status", width: 160,
        filter: true, floatingFilter: true, suppressMenu: true,
        cellRenderer: (p) => {
          const { id: appId, status } = p.data;
          if (!canChangeCandidateStatus) {
            return <span>{status}</span>;
          }
          return (
            <select
              value={status}
              onChange={(e) => updateApplicationStatus(appId, e.target.value)}
              style={{
                width: "100%", height: "100%", border: "none",
                background: STATUS_SELECT_BG,
                fontSize: "14px", cursor: "pointer",
              }}
            >
              <option value="APPLIED"     style={{ background: "white" }}>Applied</option>
              <option value="SHORTLISTED" style={{ background: "white" }}>Shortlisted</option>
              <option value="HIRED"       style={{ background: "white" }}>Hired</option>
              <option value="REJECTED"    style={{ background: "white" }}>Rejected</option>
            </select>
          );
        },
      },
      {
        headerName: "ACTIONS", width: 150, suppressMenu: true,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" },
        cellRenderer: (p) => {
          const { id: appId, name } = p.data;
          const hasResume = p.data.resumeUrl && p.data.resumeUrl !== "#";
          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", width: "100%" }}>
              <RiEyeLine
                data-no-row-click="true"
                title="View resume & overview"
                style={{ fontSize: "17px", color: "#1565c0", cursor: "pointer" }}
                onClick={() => goToCandidate(p.data)}
              />
              {job?.status !== "CLOSED" && canEditCandidate && (
                <RiPencilLine
                  data-no-row-click="true"
                  title="Edit candidate"
                  style={{ fontSize: "17px", color: "#28a745", cursor: "pointer" }}
                  onClick={() => goToCandidate(p.data, true)}
                />
              )}
              {canDownloadResume && (
                <BsDownload
                  data-no-row-click="true"
                  title={hasResume ? "Download resume" : "No resume available"}
                  style={{
                    fontSize: "15px",
                    color: hasResume ? "#1565c0" : "#bdbdbd",
                    cursor: hasResume ? "pointer" : "not-allowed",
                  }}
                  onClick={() => handleDownloadResume(p.data)}
                />
              )}
              {canDeleteCandidate && (
                <BsFillTrashFill
                  data-no-row-click="true"
                  title="Delete application"
                  style={{ fontSize: "14px", color: "#dc3545", cursor: "pointer" }}
                  onClick={() => setDeleteModal({ open: true, appId, name })}
                />
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      primaryColor, handleDownloadResume, job?.status,
      // Rebuild the columns (and their action-icon visibility) whenever the
      // permission set changes — e.g. the async permission load resolving, or
      // a live permissions_updated refresh — otherwise the Edit/Delete/Status
      // controls keep whatever visibility they had on first render.
      canEditCandidate, canDeleteCandidate, canChangeCandidateStatus,
    ]
  );

  const defaultColDef = useMemo(() => ({ cellStyle: { fontSize: "14px" } }), []);

  /* ── Loading state ───────────────────────────────── */

  if (jobLoading) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div className="page-content-wrapper d-flex justify-content-center align-items-center">
            <p style={{ color: "#9e9e9e", fontSize: "15px" }}>Loading job details…</p>
          </div>
        </div>
      </>
    );
  }

  /* ── Not found ───────────────────────────────────── */

  if (!job || jobNotFound) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div className="page-content-wrapper d-flex justify-content-center align-items-center">
            <div style={{ textAlign: "center", color: "#646569" }}>
              <p style={{ fontSize: "18px", fontWeight: 500 }}>Job not found</p>
              <button
                className="job-details-back-btn"
                style={{ margin: "10px auto" }}
                onClick={handleBack}
              >
                <ArrowLeft size={16} /> Back to Hiring
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── Derived values ──────────────────────────────── */

  const applicationStatusCounts = applications.reduce(
    (acc, app) => {
      if (acc[app.status] != null) acc[app.status] += 1;
      return acc;
    },
    { APPLIED: 0, SHORTLISTED: 0, HIRED: 0, REJECTED: 0 }
  );

  const addCandidateBlockReason = getAddCandidateBlockReason(job);

  /* ── Page tab renders ────────────────────────────── */

  const renderOverview = () => (
    <div className="job-details-overview">
      <div className="job-details-overview-section">
        <p className="job-details-overview-section-title">Job Description</p>
        <p className="job-details-description">{job.description}</p>
      </div>
      <div className="job-details-overview-section">
        <p className="job-details-overview-section-title">Required Skills</p>
        <div className="job-details-skills-list">
          {job.skills.map((skill) => (
            <span
              key={skill}
              className="job-details-skill-tag"
              style={{
                backgroundColor: primaryColor + "0d",
                borderColor: primaryColor + "40",
                color: primaryColor,
              }}
            >
              {skill}
            </span>
          ))}
        </div>
      </div>
      <div className="job-details-overview-section">
        <p className="job-details-overview-section-title">Role Details</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {[
            { label: "Designation",     value: job.designation },
            { label: "Location",        value: job.location },
            { label: "Employment Type", value: job.employmentType },
            { label: "Vacancies",       value: job.vacancies },
            { label: "Experience",      value: job.experience },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex", gap: "12px" }}>
              <span style={{ width: "150px", fontSize: "13px", color: "#646569", fontWeight: 500 }}>{label}</span>
              <span style={{ fontSize: "14px", color: "#212121" }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderImportTab = (tabId) => (
    <ResumeImportPanel
      jobId={id}
      status={IMPORT_TAB_STATUS[tabId]}
      sessionId={importSessionId}
      primaryColor={primaryColor}
      onSaved={handleBulkSaved}
      onCountsChange={handleImportCountsChange}
      jobClosed={job?.status === "CLOSED"}
    />
  );

  const renderApplications = () => (
    <div className="job-details-grid-wrapper">
      {appsLoading ? (
        <div style={{ padding: "60px 20px", textAlign: "center", color: "#9e9e9e" }}>
          <p style={{ fontSize: "15px" }}>Loading applications…</p>
        </div>
      ) : applications.length === 0 ? (
        <div style={{ padding: "60px 20px", textAlign: "center", color: "#646569" }}>
          <p style={{ fontSize: "15px" }}>No applications yet for this job.</p>
        </div>
      ) : (
        <div
          className="ag-theme-alpine"
          style={{ height: "60vh", fontSize: "14px", fontFamily: "Arial, Helvetica, sans-serif" }}
        >
          <AgGridReact
            rowData={applications}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination
            paginationPageSize={20}
            suppressCellFocus
            enableCellTextSelection={false}
            suppressRowClickSelection
            onRowClicked={handleRowClicked}
            rowStyle={{ cursor: "pointer" }}
          />
        </div>
      )}
    </div>
  );

  const getTabCount = (tabId) => {
    if (tabId === "APPLICATIONS") return applications.length;
    if (IMPORT_TAB_IDS.has(tabId)) return importCounts[tabId] ?? 0;
    return null;
  };

  /* ─────────────────────────────────────────────────── */

  return (
    <>
      <Header />
      <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
        <LeftNavBar />
        <div className="page-content-wrapper hiring-detail-fade-in">

          <PageHeader>
            <PageHeaderName>
              <ArrowLeft
                size={20}
                onClick={handleBack}
                style={{ cursor: "pointer", marginRight: "8px" }}
              />
              {getHiringListLabel(fromHiringTab)}
            </PageHeaderName>
          </PageHeader>

          {/* Summary card */}
          <div className="job-details-summary-card">
            <div className="job-details-summary-header">
              <div className="job-details-title-row">
                <div>
                  <p className="job-details-title">{job.title}</p>
                  <p className="job-details-dept">{job.designation}</p>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span className={`job-status-badge job-status-${getDisplayStatus(job).toLowerCase()}`}>
                    {getDisplayStatus(job)}
                  </span>
                  <span className="job-type-badge">{job.employmentType}</span>
                </div>
              </div>

              <div className="job-details-float-actions">
                {canCreateCandidate && !getAddCandidateBlockReason(job) && (
                  <PrimaryButton
                    type="button"
                    width="auto"
                    onClick={() => {
                      const blockReason = getAddCandidateBlockReason(job);
                      if (blockReason) {
                        toast.error(blockReason);
                        return;
                      }
                      setIsReferOpen(true);
                    }}
                  >
                    <UserPlus size={16} style={{ marginRight: "6px", verticalAlign: "-3px" }} />
                    Refer a Candidate
                  </PrimaryButton>
                )}
                {job.status !== "CLOSED" && (
                  <SecondaryButton type="button" width="auto" onClick={() => setIsShareOpen(true)}>
                    <Share2 size={16} style={{ marginRight: "6px", verticalAlign: "-3px" }} />
                    Share
                  </SecondaryButton>
                )}
              </div>

              <div className="job-details-meta-row">
                <span className="job-details-meta-pill">
                  <MdLocationOn className="job-details-meta-icon" />{job.location}
                </span>
                <span className="job-details-meta-pill">
                  <MdOutlinePeople className="job-details-meta-icon" />
                  {job.vacancies} {job.vacancies === 1 ? "Vacancy" : "Vacancies"}
                </span>
                <span className="job-details-meta-pill">
                  <MdWorkOutline className="job-details-meta-icon" />{job.experience}
                </span>
              </div>

              {job.publishedDate && (
                <div className="job-details-posted-date">
                  <MdCalendarToday className="job-details-meta-icon" />
                  Posted on {formatPostedDate(job.publishedDate)}
                </div>
              )}
            </div>

            {canViewCandidates && (
            <div className="job-details-stat-strip">
              {[
                {
                  label: "Total Applications",
                  value:
                    applicationStatusCounts.APPLIED +
                    applicationStatusCounts.SHORTLISTED +
                    applicationStatusCounts.HIRED +
                    applicationStatusCounts.REJECTED,
                },
                { label: "Applied",     value: applicationStatusCounts.APPLIED },
                { label: "Shortlisted", value: applicationStatusCounts.SHORTLISTED },
                { label: "Hired",       value: applicationStatusCounts.HIRED },
                { label: "Rejected",    value: applicationStatusCounts.REJECTED },
              ].map(({ label, value }) => (
                <div key={label} className="job-details-stat-item">
                  <span className="job-details-stat-value">{value}</span>
                  <span className="job-details-stat-label">{label}</span>
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Content box */}
          <div className="job-details-content-box">
            <div className="job-details-tab-bar">
              <div className="job-details-tabs-left">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    style={
                      activeTab === tab.id
                        ? { color: primaryColor, borderBottom: `3px solid ${primaryColor}` }
                        : {}
                    }
                    className={activeTab === tab.id ? "job-details-active-tab" : "job-details-tab"}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab.label}
                    {getTabCount(tab.id) != null && (
                      <span
                        style={{
                          marginLeft: "6px", fontSize: "12px",
                          color: activeTab === tab.id ? primaryColor : "#9e9e9e",
                        }}
                      >
                        ({getTabCount(tab.id)})
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {activeTab === "APPLICATIONS" && (
                <div className="job-details-tab-actions">
                  <StyledSelectGroup style={{ marginRight: "10px" }}>
                    <StyledSelectContainer>
                      <StyledSelect
                        width="210px"
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                      >
                        {SORT_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </StyledSelect>
                      <StyledLabel hasValue className="user-label">Sort By</StyledLabel>
                    </StyledSelectContainer>
                  </StyledSelectGroup>
                  {(canCreateCandidate || canImportResume) && (
                    <div
                      className="job-add-candidate-dropdown"
                      ref={addCandidateDropdownRef}
                    >
                      <PrimaryButton
                        type="button"
                        width="auto"
                        disabled={Boolean(addCandidateBlockReason)}
                        title={addCandidateBlockReason || undefined}
                        style={{
                          padding: "0 14px",
                          margin: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onClick={() => {
                          if (addCandidateBlockReason) return;
                          setAddCandidateMenuOpen((open) => !open);
                        }}
                      >
                        + Add Candidate
                        <MdOutlineKeyboardArrowDown size={18} />
                      </PrimaryButton>
                      {addCandidateBlockReason && (
                        <p className="job-add-candidate-future-note">
                          <BsExclamationCircleFill size={12} />
                          {addCandidateBlockReason}
                        </p>
                      )}
                      {!addCandidateBlockReason && addCandidateMenuOpen && (
                        <ul className="job-add-candidate-menu">
                          {canCreateCandidate && (
                            <li>
                              <button
                                type="button"
                                className="job-add-candidate-menu-item"
                                onClick={openManualAddCandidate}
                              >
                                Add Manually
                              </button>
                            </li>
                          )}
                          {canImportResume && (
                            <li>
                              <button
                                type="button"
                                className="job-add-candidate-menu-item"
                                onClick={openImportResumes}
                              >
                                Add via Resumes
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div key={activeTab} className="hiring-tab-content-fade">
              {activeTab === "OVERVIEW"     && renderOverview()}
              {activeTab === "APPLICATIONS" && canViewCandidates && renderApplications()}
              {canImportResume && IMPORT_TAB_IDS.has(activeTab) && renderImportTab(activeTab)}
            </div>
          </div>

        </div>
      </div>

      {/* Add Candidate — SlidingModal */}
      <SlidingModal
        isModalOpen={isAddCandidateOpen}
        modalName="Add Candidate"
        closeBtn={() => setIsAddCandidateOpen(false)}
      >
        <AddCandidateModal
          jobId={id}
          onSuccess={handleApplicationCreated}
          onCancel={() => setIsAddCandidateOpen(false)}
        />
      </SlidingModal>

      <UploadResumeModal
        jobId={id}
        isOpen={isUploadResumeOpen}
        onClose={() => setIsUploadResumeOpen(false)}
        onParsed={handleParsedResume}
      />

      <ResumeReviewModal
        isOpen={isResumeReviewOpen}
        parsedData={parsedResumeData}
        jobId={id}
        onSuccess={handleReviewSaved}
        onClose={() => {
          setIsResumeReviewOpen(false);
          setParsedResumeData(null);
        }}
      />

      <BulkUploadModal
        jobId={id}
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onSaved={handleBulkSaved}
      />

      <ImportResumesModal
        jobId={id}
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImportComplete={handleImportComplete}
      />

      <ReferCandidateModal
        jobId={id}
        isOpen={isReferOpen}
        onClose={() => setIsReferOpen(false)}
        onSuccess={() => { setIsReferOpen(false); triggerAppsRefresh(); }}
      />

      <ShareJobModal
        job={job}
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
      />

      {/* Delete Confirmation */}
      <PopupModal
        showModal={deleteModal.open}
        modalName="Delete Application"
        closeBtn={() => setDeleteModal({ open: false, appId: null, name: "" })}
      >
        <div style={{ minWidth: "320px" }}>
          <p style={{ fontSize: "14px", color: "#212121", marginBottom: "20px" }}>
            Are you sure you want to delete the application from{" "}
            <strong>{deleteModal.name}</strong>?
          </p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <SecondaryButton
              onClick={() => setDeleteModal({ open: false, appId: null, name: "" })}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={async () => {
                if (!canDeleteCandidate) {
                  setDeleteModal({ open: false, appId: null, name: "" });
                  return;
                }
                try {
                  const res  = await deleteHiringApplication(id, deleteModal.appId);
                  const data = await res.json();
                  if (!data.status) throw new Error(data.message || "Delete failed");
                  setApplications((prev) => prev.filter((a) => a.id !== deleteModal.appId));
                  toast.success("Application deleted");
                } catch {
                  toast.error("Failed to delete application. Please try again.");
                } finally {
                  setDeleteModal({ open: false, appId: null, name: "" });
                }
              }}
            >
              Delete
            </PrimaryButton>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default JobDetailsPage;
